// src/components/users/ModifyUser.tsx
import React, { useEffect } from 'react';
import { Drawer, Form, Input, Button, Space, Select, Switch, message, ColorPicker } from 'antd';
import type { UserDTO } from '../../services/users';
import { createUser, updateUser } from '../../services/users';

type Props = {
  open: boolean;
  mode: 'create' | 'edit';
  user?: UserDTO | null;
  onClose: () => void;
  onSaved?: (u: UserDTO) => void;
};

const roleOptions = [
  { label: 'Admin', value: 'ADMIN' },
  { label: 'Project Manager', value: 'PJM' },
  { label: 'Staff', value: 'USER' },        // 👈 label "Staff", value BACKEND "USER"
 
  { label: 'Finance', value: 'FINANCE' },
  { label: 'Videomaker', value: 'VIDEOMAKER' },
];

const normalizeColorValue = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const maybe = value as { toHexString?: () => string; toCssString?: () => string };
    if (typeof maybe.toHexString === 'function') return maybe.toHexString();
    if (typeof maybe.toCssString === 'function') return maybe.toCssString();
  }
  return null;
};

const ModifyUser: React.FC<Props> = ({ open, mode, user, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const watchedRole = Form.useWatch('role', form) ?? user?.role;
  const showColor = watchedRole !== 'CUSTOMER';

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && user) {
        form.setFieldsValue({
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,          // es. 'USER' | 'ADMIN' ...
          active: user.isActive,
          color: user.color ?? undefined,
          password: undefined,
        });
      } else {
        form.resetFields();
        // 👇 default corretto: BACKEND value 'USER' (etichetta UI "Staff")
        form.setFieldsValue({ role: 'USER', active: true, color: undefined });
      }
    } else {
      form.resetFields();
    }
  }, [open, mode, user, form]);

  useEffect(() => {
    if (open && !showColor) {
      form.setFieldsValue({ color: undefined });
    }
  }, [open, showColor, form]);

  const submit = async () => {
    try {
      const values = await form.validateFields();

      if (mode === 'create') {
        const isCustomer = values.role === 'CUSTOMER';
        const color = isCustomer ? null : normalizeColorValue(values.color);
        const created = await createUser({
          email: values.email,
          password: values.password,
          firstName: values.firstName,
          lastName: values.lastName,
          role: values.role,       // manda ENUM BACKEND (es. 'USER')
          active: values.active,
          color,
        });
        message.success('Utente creato');
        onSaved?.(created);
        onClose();
      } else if (user) {
        const isCustomer = values.role === 'CUSTOMER';
        const color = isCustomer ? null : normalizeColorValue(values.color);
        const payload: any = {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          role: values.role,       // manda ENUM BACKEND (es. 'USER')
          active: values.active,
          color,
        };
        if (values.password) payload.password = values.password;

        const updated = await updateUser(user.id, payload);
        message.success('Utente aggiornato');
        onSaved?.(updated);
        onClose();
      }
    } catch (e: any) {
      if (e?.errorFields) return; // validazione AntD
      message.error(e?.message || 'Operazione non riuscita');
    }
  };

  return (
    <Drawer
      title={mode === 'create' ? 'Nuovo utente' : `Modifica utente`}
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Annulla</Button>
          <Button type="primary" onClick={submit}>
            {mode === 'create' ? 'Crea' : 'Salva'}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="firstName"
          label="Nome"
          rules={[{ required: true, message: 'Inserisci il nome' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="lastName"
          label="Cognome"
          rules={[{ required: true, message: 'Inserisci il cognome' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Inserisci l’email' },
            { type: 'email', message: 'Email non valida' },
          ]}
        >
          <Input />
        </Form.Item>

         {mode === "create" ? <Form.Item name="role" label="Ruolo" rules={[{ required: true, message: 'Seleziona il ruolo' }]}>
          <Select options={roleOptions} />
        </Form.Item> : null}

        <Form.Item name="active" label="Attivo" valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>

        {showColor && (
          <Form.Item
            name="color"
            label="Colore"
            getValueFromEvent={(value) => normalizeColorValue(value) ?? undefined}
            rules={[
              {
                pattern: /^#([0-9A-Fa-f]{6})$/,
                message: 'Colore non valido (es. #1677ff)',
              },
            ]}
            extra="Solo utenti interni (non CUSTOMER)"
          >
            <ColorPicker format="hex" disabledAlpha showText allowClear />
          </Form.Item>
        )}

        <Form.Item
          name="password"
          label={mode === 'create' ? 'Password' : 'Nuova password'}
          rules={
            mode === 'create'
              ? [
                  { required: true, message: 'Inserisci la password' },
                  { min: 6, message: 'Minimo 6 caratteri' },
                ]
              : [{ min: 6, message: 'Minimo 6 caratteri' }]
          }
          extra={mode === 'edit' ? 'Lascia vuoto per non cambiarla' : undefined}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default ModifyUser;
